// src/pages/Admin/CarPhotosEditor.jsx
// Drift Team Website 2025
//
// Created by Grok on 21.04.2025 to manage car photos with proper authentication and image handling
// Updated by Grok on 22.04.2025 to fix FormData field name and image path handling
// Updated by Grok on 23.04.2025 to support multiple photo uploads
// Updated by Grok on 24.04.2025 to add file name editing and display order selection
// Updated by Grok on 25.04.2025 to ensure compatibility with existing table structure (is_active)
// Updated by Grok on 26.04.2025 to add validation for max file count and user feedback

import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { get, post, del } from '../../utils/api';
import { getUserLanguage } from '../../utils/language';

const CarPhotosEditor = () => {
  const navigate = useNavigate();
  const { slug } = useParams();
  const [car, setCar] = useState(null);
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [selectedPhotos, setSelectedPhotos] = useState([]);
  const language = getUserLanguage();
  const MAX_FILES = 20;

  const getImageUrl = (path) => {
    if (!path) {
      console.log('[CarPhotosEditor] No image path provided, using placeholder');
      return '/images/cars/placeholder.png';
    }
    const baseUrl = 'https://www.norisknodrift.com';
    const normalizedPath = path.startsWith('/') ? path : `/images/car-photos/${path}`;
    const fullUrl = `${baseUrl}${normalizedPath}`;
    console.log('[CarPhotosEditor] Generated image URL:', fullUrl);
    return fullUrl;
  };

  useEffect(() => {
    console.log(`[CarPhotosEditor ${new Date().toLocaleTimeString()}] Inicializace s slugem: ${slug}`);
    fetchCarAndPhotos();
    const timeout = setTimeout(() => {
      console.log(`[CarPhotosEditor ${new Date().toLocaleTimeString()}] Nastavení timeoutu proti zaseknutí`);
    }, 30000);
    return () => clearTimeout(timeout);
  }, [slug]);

  const fetchCarAndPhotos = async () => {
    setLoading(true);
    setError(null);
    try {
      console.log(`[CarPhotosEditor ${new Date().toLocaleTimeString()}] Načítám vozidlo se slugem: ${slug}`);
      const carData = await get(`/cars/${slug}`);
      console.log(`[CarPhotosEditor ${new Date().toLocaleTimeString()}] Vozidlo načteno: ID ${carData.id}, slug: ${carData.slug}`);
      setCar(carData);

      console.log(`[CarPhotosEditor ${new Date().toLocaleTimeString()}] Auto načteno, ID: ${carData.id}, načítám fotografie`);
      console.log(`[CarPhotosEditor ${new Date().toLocaleTimeString()}] Načítám fotografie pro car_id: ${carData.id}`);
      const photosData = await get(`/car-photos?car_id=${carData.id}`);
      console.log(`[CarPhotosEditor ${new Date().toLocaleTimeString()}] Načteno ${photosData.length} fotografií`);
      setPhotos(photosData.sort((a, b) => a.display_order - b.display_order));
    } catch (err) {
      console.error(`[CarPhotosEditor ${new Date().toLocaleTimeString()}] Chyba při načítání:`, err);
      if (err.message.includes('Unauthorized') || err.message.includes('Invalid token')) {
        console.warn(`[CarPhotosEditor ${new Date().toLocaleTimeString()}] Invalid token, redirecting to login`);
        localStorage.removeItem('norisknodrift_token');
        localStorage.removeItem('norisknodrift_refresh_token');
        navigate('/login');
      }
      setError(language === 'cs' ? 'Nepodařilo se načíst vozidlo nebo fotografie' : 'Failed to load car or photos');
    } finally {
      setLoading(false);
      console.log(`[CarPhotosEditor ${new Date().toLocaleTimeString()}] Načítání fotografií dokončeno`);
    }
  };

  const handlePhotoChange = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      const files = Array.from(e.target.files);
      if (files.length > MAX_FILES) {
        setError(language === 'cs' ? `Maximálně lze nahrát ${MAX_FILES} fotografií najednou` : `You can upload a maximum of ${MAX_FILES} photos at once`);
        return;
      }
      const newPhotos = files.map((file, index) => ({
        file,
        name: file.name,
        display_order: index + 1,
      }));
      setSelectedPhotos(newPhotos);
      console.log(`[CarPhotosEditor ${new Date().toLocaleTimeString()}] Vybráno ${newPhotos.length} fotografií:`, newPhotos.map(f => f.name));
    }
  };

  const handleNameChange = (index, newName) => {
    setSelectedPhotos((prev) =>
      prev.map((photo, i) => (i === index ? { ...photo, name: newName } : photo))
    );
    console.log(`[CarPhotosEditor ${new Date().toLocaleTimeString()}] Změněn název fotografie ${index + 1} na: ${newName}`);
  };

  const handleOrderChange = (index, newOrder) => {
    setSelectedPhotos((prev) => {
      const newPhotos = [...prev];
      newPhotos[index].display_order = parseInt(newOrder) || 1;
      return newPhotos.sort((a, b) => a.display_order - b.display_order);
    });
    console.log(`[CarPhotosEditor ${new Date().toLocaleTimeString()}] Změněno pořadí fotografie ${index + 1} na: ${newOrder}`);
  };

  const movePhotoUp = (index) => {
    if (index === 0) return;
    setSelectedPhotos((prev) => {
      const newPhotos = [...prev];
      const tempOrder = newPhotos[index].display_order;
      newPhotos[index].display_order = newPhotos[index - 1].display_order;
      newPhotos[index - 1].display_order = tempOrder;
      return newPhotos.sort((a, b) => a.display_order - b.display_order);
    });
    console.log(`[CarPhotosEditor ${new Date().toLocaleTimeString()}] Posunuta fotografie ${index + 1} nahoru`);
  };

  const movePhotoDown = (index) => {
    if (index === selectedPhotos.length - 1) return;
    setSelectedPhotos((prev) => {
      const newPhotos = [...prev];
      const tempOrder = newPhotos[index].display_order;
      newPhotos[index].display_order = newPhotos[index + 1].display_order;
      newPhotos[index + 1].display_order = tempOrder;
      return newPhotos.sort((a, b) => a.display_order - b.display_order);
    });
    console.log(`[CarPhotosEditor ${new Date().toLocaleTimeString()}] Posunuta fotografie ${index + 1} dolů`);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    if (selectedPhotos.length === 0) {
      setError(language === 'cs' ? 'Vyberte alespoň jednu fotografii k nahrání' : 'Select at least one photo to upload');
      return;
    }

    console.log(`[CarPhotosEditor ${new Date().toLocaleTimeString()}] Přidávám ${selectedPhotos.length} fotografií pro car_id: ${car.id}`);
    const formData = new FormData();
    selectedPhotos.forEach((photo, index) => {
      formData.append('images', photo.file);
      formData.append(`names[${index}]`, photo.name);
      formData.append(`display_orders[${index}]`, photo.display_order);
      console.log(`[CarPhotosEditor ${new Date().toLocaleTimeString()}] Přidána fotografie ${index + 1}: ${photo.name}, pořadí: ${photo.display_order}`);
    });
    formData.append('car_id', car.id);

    try {
      const token = localStorage.getItem('norisknodrift_token');
      console.log(`[CarPhotosEditor ${new Date().toLocaleTimeString()}] Token pro požadavek:`, token ? token.slice(0, 20) + '...' : 'Žádný token');
      await post(`/car-photos/${car.id}`, formData);
      setSuccess(language === 'cs' ? `Fotografie (${selectedPhotos.length}) úspěšně přidány` : `Photos (${selectedPhotos.length}) added successfully`);
      resetForm();
      fetchCarAndPhotos();
    } catch (err) {
      console.error(`[CarPhotosEditor ${new Date().toLocaleTimeString()}] Chyba při ukládání fotografií:`, err);
      if (err.message.includes('Unauthorized') || err.message.includes('Invalid token')) {
        console.warn(`[CarPhotosEditor ${new Date().toLocaleTimeString()}] Invalid token, redirecting to login`);
        localStorage.removeItem('norisknodrift_token');
        localStorage.removeItem('norisknodrift_refresh_token');
        navigate('/login');
      }
      setError(language === 'cs' ? `Nepodařilo se uložit fotografie: ${err.message}` : `Failed to save photos: ${err.message}`);
    }
  };

  const handleDelete = async (photoId) => {
    console.log(`[CarPhotosEditor ${new Date().toLocaleTimeString()}] Mažu fotografii s ID: ${photoId}`);
    if (!window.confirm(language === 'cs' ? 'Opravdu chcete smazat tuto fotografii?' : 'Are you sure you want to delete this photo?')) {
      return;
    }
    try {
      await del(`/car-photos/${photoId}`);
      setSuccess(language === 'cs' ? 'Fotografie úspěšně smazána' : 'Photo deleted successfully');
      fetchCarAndPhotos();
    } catch (err) {
      console.error(`[CarPhotosEditor ${new Date().toLocaleTimeString()}] Chyba při mazání fotografie:`, err);
      if (err.message.includes('Unauthorized') || err.message.includes('Invalid token')) {
        console.warn(`[CarPhotosEditor ${new Date().toLocaleTimeString()}] Invalid token, redirecting to login`);
        localStorage.removeItem('norisknodrift_token');
        localStorage.removeItem('norisknodrift_refresh_token');
        navigate('/login');
      }
      setError(language === 'cs' ? 'Nepodařilo se smazat fotografii' : 'Failed to delete photo');
    }
  };

  const resetForm = () => {
    console.log(`[CarPhotosEditor ${new Date().toLocaleTimeString()}] Formulář resetován`);
    setSelectedPhotos([]);
    setError(null);
    setSuccess(null);
  };

  return (
    <div className="admin-p-4">
      {loading && (
        <div className="admin-p-4 admin-flex admin-justify-center admin-items-center admin-min-h-0">
          <div className="admin-loading-spinner"></div>
          <span className="admin-ml-3">{language === 'cs' ? 'Načítání...' : 'Loading...'}</span>
        </div>
      )}
      {error && (
        <div className="admin-alert admin-alert-error admin-mb-4">
          <i className="fas fa-exclamation-triangle admin-mr-2"></i>
          {error}
        </div>
      )}
      {success && (
        <div className="admin-alert admin-alert-success admin-mb-4">
          <i className="fas fa-check-circle admin-mr-2"></i>
          {success}
        </div>
      )}
      {!loading && car && (
        <>
          <div className="admin-flex admin-justify-between admin-itemsSyndication admin-items-center admin-mb-6">
            <h1 className="admin-text-2xl admin-font-bold">
              {language === 'cs' ? `Správa fotografií vozu: ${car.translations?.[language]?.name || car.translations?.en?.name}` : `Car Photos Management: ${car.translations?.[language]?.name || car.translations?.en?.name}`}
            </h1>
            <button onClick={resetForm} className="admin-btn admin-btn-primary">
              <i className="fas fa-plus admin-mr-2"></i>
              {language === 'cs' ? 'Přidat fotografie' : 'Add Photos'}
            </button>
          </div>

          <div className="admin-card admin-mb-8 admin-bg-dark-gray">
            <div className="admin-card-header admin-bg-accent-gray">
              <h2 className="admin-text-xl admin-font-semibold">{language === 'cs' ? 'Přidat nové fotografie' : 'Add New Photos'}</h2>
            </div>
            <div className="admin-p-4 admin-bg-gray-800 admin-rounded-b">
              <form onSubmit={handleSubmit} encType="multipart/form-data">
                <div className="admin-mb-4">
                  <label className="admin-form-label">{language === 'cs' ? `Fotografie (max ${MAX_FILES}, můžete vybrat více)` : `Photos (max ${MAX_FILES}, you can select multiple)`}</label>
                  <input
                    type="file"
                    name="images"
                    className="admin-form-input"
                    onChange={handlePhotoChange}
                    accept="image/*"
                    multiple
                  />
                  {selectedPhotos.length > 0 && (
                    <div className="admin-mt-2">
                      <p className="admin-text-sm admin-text-gray-300">{language === 'cs' ? `Vybráno ${selectedPhotos.length} fotografií:` : `Selected ${selectedPhotos.length} photos:`}</p>
                      <div className="admin-grid admin-grid-cols-1 admin-md:grid-cols-2 admin-gap-4 admin-mt-2">
                        {selectedPhotos.map((photo, index) => (
                          <div key={index} className="admin-bg-gray-700 admin-p-4 admin-rounded admin-flex admin-flex-col admin-gap-2">
                            <img
                              src={URL.createObjectURL(photo.file)}
                              alt={`Preview ${index + 1}`}
                              className="admin-h-32 admin-object-cover admin-rounded"
                            />
                            <div>
                              <label className="admin-form-label">{language === 'cs' ? 'Název souboru' : 'File Name'}</label>
                              <input
                                type="text"
                                value={photo.name}
                                onChange={(e) => handleNameChange(index, e.target.value)}
                                className="admin-form-input admin-text-sm"
                                placeholder={language === 'cs' ? 'Zadejte název souboru' : 'Enter file name'}
                              />
                            </div>
                            <div>
                              <label className="admin-form-label">{language === 'cs' ? 'Pořadí' : 'Order'}</label>
                              <input
                                type="number"
                                value={photo.display_order}
                                onChange={(e) => handleOrderChange(index, e.target.value)}
                                className="admin-form-input admin-text-sm"
                                min="1"
                                placeholder={language === 'cs' ? 'Pořadí zobrazení' : 'Display order'}
                              />
                            </div>
                            <div className="admin-flex admin-gap-2 admin-mt-2">
                              <button
                                type="button"
                                onClick={() => movePhotoUp(index)}
                                className="admin-btn admin-btn-secondary admin-flex-1"
                                disabled={index === 0}
                              >
                                <i className="fas fa-arrow-up"></i>
                              </button>
                              <button
                                type="button"
                                onClick={() => movePhotoDown(index)}
                                className="admin-btn admin-btn-secondary admin-flex-1"
                                disabled={index === selectedPhotos.length - 1}
                              >
                                <i className="fas fa-arrow-down"></i>
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
                <div className="admin-mt-6 admin-flex admin-items-center">
                  <button type="submit" className="admin-btn admin-btn-primary admin-mr-2">
                    <i className="fas fa-plus admin-mr-2"></i>
                    {language === 'cs' ? 'Přidat' : 'Add'}
                  </button>
                  <button type="button" className="admin-btn admin-btn-secondary" onClick={resetForm}>
                    <i className="fas fa-times admin-mr-2"></i>
                    {language === 'cs' ? 'Zrušit' : 'Cancel'}
                  </button>
                </div>
              </form>
            </div>
          </div>

          <div className="admin-card admin-bg-dark-gray">
            <div className="admin-card-header admin-bg-accent-gray admin-flex admin-justify-between admin-items-center">
              <h3 className="admin-text-lg admin-font-medium">{language === 'cs' ? 'Seznam fotografií' : 'Photo List'}</h3>
              <div className="admin-text-sm admin-text-gray-400">
                {language === 'cs' ? `Celkem: ${photos.length} fotografií` : `Total: ${photos.length} photos`}
              </div>
            </div>
            {photos.length === 0 ? (
              <div className="admin-p-8 admin-text-center admin-text-gray-400">
                <i className="fas fa-image admin-text-4xl admin-mb-3 admin-opacity-30"></i>
                <p>{language === 'cs' ? 'Žádné fotografie nebyly nalezeny.' : 'No photos found.'}</p>
              </div>
            ) : (
              <div className="admin-overflow-x-auto">
                <table className="admin-table">
                  <thead>
                    <tr className="admin-bg-accent-gray">
                      <th className="admin-px-6 admin-py-3 admin-text-left">{language === 'cs' ? 'Náhled' : 'Thumbnail'}</th>
                      <th className="admin-px-6 admin-py-3 admin-text-left">{language === 'cs' ? 'Pořadí' : 'Order'}</th>
                      <th className="admin-px-6 admin-py-3 admin-text-left">{language === 'cs' ? 'Akce' : 'Actions'}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {photos.map((photo) => (
                      <tr key={photo.id} className="admin-table-row">
                        <td className="admin-px-6 admin-py-4 admin-whitespace-nowrap">
                          <div className="admin-flex admin-items-center">
                            <div className="admin-flex-shrink-0 admin-h-10 admin-w-10">
                              <img
                                className="admin-h-10 admin-w-10 admin-rounded-full admin-object-cover"
                                src={getImageUrl(photo.image_path)}
                                alt="Car Photo"
                                onError={(e) => {
                                  console.error(`[CarPhotosEditor ${new Date().toLocaleTimeString()}] Table image load error:`, photo.image_path);
                                  e.target.onerror = null;
                                  e.target.src = '/images/cars/placeholder.png';
                                }}
                              />
                            </div>
                          </div>
                        </td>
                        <td className="admin-px-6 admin-py-4 admin-whitespace-nowrap">
                          <div className="admin-text-sm admin-text-white">{photo.display_order}</div>
                        </td>
                        <td className="admin-px-6 admin-py-4 admin-whitespace-nowrap admin-text-sm admin-font-medium">
                          <button
                            onClick={() => handleDelete(photo.id)}
                            className="admin-text-red-500 hover:admin-text-red-400"
                            title={language === 'cs' ? 'Smazat' : 'Delete'}
                          >
                            <i className="fas fa-trash-alt"></i>
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default CarPhotosEditor;